import { Questionnaire } from '@/assets/types'
import { defineStore } from 'pinia'
import { computed, reactive } from 'vue'
import { handleSubscription } from './helper'
import { auth, getToken } from '@/assets/utils'
export const useQuestionnairesStore = defineStore('questionnaires', () => {
    const questionnaires = reactive({
        current: [] as Questionnaire[],

        async init() {
            await this.fetch()
        },

        async fetch() {
            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/questionnaires`,
                location.href
            )

            url.searchParams.set("order", 'desc')

            const res = await auth.get(
                url.toString(),
                getToken()
            )

            if (res.status == 200) {
                data = res.data ?? []
            }

            this.current = data.map(questionnaireFromAPI)
            return this.current as Questionnaire[]
        },

        async fetchOne(id: string) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/questionnaire/${id}`,
                location.href
            )

            const res = await auth.get(
                url.toString(),
                getToken()
            )

            let data = null

            if (res.status === 200) {
                data = questionnaireFromAPI(res.data)
            }

            return data
        },

        async create(q: Questionnaire) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/questionnaires`,
                location.href
            )

            const res = await auth.post(
                url.toString(),
                questionnaireToAPI(q),
                getToken()
            )

            if (res.status == 200) {
                return res.data
            } else {
                return null
            }
        },

        async edit(q: Questionnaire) {
            const questionnaire: any = {
                ...q,
            }
            delete questionnaire.id

            questionnaire.questions = q.questions

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/questionnaire/${q.id}`,
                location.href
            )

            const res = await auth.put(
                url.toString(),
                questionnaire,
                getToken()
            )

            if (res.status == 200) {
                return res.data
            } else {
                return null
            }
        },

        async delete(id: string) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/questionnaire/${id}`,
                location.href
            )

            const res = await auth.delete(
                url.toString(),
                getToken()
            )

            if (res.status == 200) {
                return res.data
            } else {
                return null
            }
        },

        sub(id?: string) {
            if (!id) {
                return handleSubscription(
                    'questionnaires',
                    {
                        create: (doc) => {
                            const questionnaire = questionnaireFromAPI(doc)
                            this.current.unshift(questionnaire)
                        },
                        update: (doc) => {
                            const questionnaire = questionnaireFromAPI(doc)
                            const index = this.current.findIndex((q: Questionnaire) => q.id === questionnaire.id)
                            this.current.splice(index, 1, questionnaire)
                        },
                        delete: (doc) => {
                            const index = this.current.findIndex((q: Questionnaire) => q.id === doc.id)
                            this.current.splice(index, 1)
                        },
                    }
                )
            } else {
                return handleSubscription(
                    `questionnaires`,
                    {
                        create: () => { },
                        update: (doc) => {
                            if (doc.id !== id) return;
                            const questionnaire = questionnaireFromAPI(doc)
                            const index = this.current.findIndex((q: Questionnaire) => q.id === questionnaire.id)
                            this.current.splice(index, 1, questionnaire)
                        },
                        delete: (doc) => {
                            if (doc.id !== id) return;
                            const index = this.current.findIndex((q: Questionnaire) => q.id === doc.id)
                            this.current.splice(index, 1)
                        },
                    }
                )
            }
        }
    })

    function addQuestionnaire(q: Questionnaire) {
        return questionnaires.create(q)
    }

    function editQuestionnaire(updated: Questionnaire) {
        return questionnaires.edit(updated)
    }

    function deleteQuestionnaire(id: string) {
        return questionnaires.delete(id)
    }

    // Computed getter: returns a function to get by id (reactive)
    const getQuestionnaire = computed(() => async (id: string) => {
        return await questionnaires.fetchOne(id)
    })

    return {
        questionnaires,
        addQuestionnaire,
        editQuestionnaire,
        deleteQuestionnaire,
        getQuestionnaire,
    }
})


function questionnaireFromAPI(response: any) {
    return {
        id: response.id,
        title: response.title,
        questions: response.questions,
    } as Questionnaire
}

function questionnaireToAPI(q: Questionnaire) {
    return {
        title: q.title,
        questions: q.questions,
    }
}